import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import color from "color";
import { useLayoutEffect } from "react";
import { View } from "react-native";
import { SystemBars } from "react-native-edge-to-edge";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppContext } from "./AppContext";
import { ScrollView } from "./ScrollView";
import { Text } from "./Text";
import { useDefaultHeaderHeight } from "../lib/getDefaultHeaderHeight";
import { useTheme } from "../styles";
/**
 * @nosync
 */ export function RoundedSectionNavigationOptions(param) {
    var defaultHeaderHeight = param.defaultHeaderHeight, headerTintColor = param.headerTintColor, tintColor = param.tintColor, backgroundColor = param.backgroundColor;
    return {
        headerTintColor: tintColor,
        headerTitleStyle: {
            color: headerTintColor !== null && headerTintColor !== void 0 ? headerTintColor : tintColor,
            fontFamily: 'OpenSansSemiBold'
        },
        headerTitle: '',
        headerShadowVisible: false,
        headerStyle: {
            backgroundColor: backgroundColor,
            height: defaultHeaderHeight
        },
        // Keep this for backwards compatibility pre RoundedSection cleanup. However, we shouldn't rely on
        // headerRight being reset here and instead the caller should manually specify headerRight: undefined
        // if that's the desired behavior
        headerRight: undefined
    };
}
/**
 * @nosync
 */ export function RoundedSectionTopChild(props) {
    return /*#__PURE__*/ _jsx(View, {
        style: {
            backgroundColor: props.backgroundColor,
            marginTop: -20,
            marginHorizontal: -20,
            padding: 20,
            borderTopStartRadius: 30,
            borderTopEndRadius: 30,
            marginBottom: 20
        },
        children: props.children
    });
}
/**
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-103}
 */ export function RoundedSection(props) {
    var theme = useTheme().theme;
    var setOptions = useNavigation().setOptions;
    var navigationContainer = useAppContext().navigationContainer;
    var isDark = color(props.color).darken(0.1).isDark();
    var defaultHeaderHeight = useDefaultHeaderHeight();
    var isFocused = useIsFocused();
    var insets = useSafeAreaInsets();
    useLayoutEffect(function() {
        if (props.noScrollView) {
            setOptions({
                headerTitle: ''
            });
        }
        if (props.applyHeaderOptions) {
            setOptions(RoundedSectionNavigationOptions({
                backgroundColor: props.color,
                tintColor: props.tintColor || (isDark ? 'white' : theme.color.gray100),
                defaultHeaderHeight: defaultHeaderHeight
            }));
        }
    }, [
        theme,
        isDark,
        props.noScrollView,
        setOptions,
        props.applyHeaderOptions,
        props.color,
        props.secondaryColor,
        props.tintColor,
        defaultHeaderHeight
    ]);
    var inner = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(View, {
                style: {
                    backgroundColor: props.color,
                    paddingTop: 10,
                    paddingBottom: 20,
                    paddingHorizontal: 10
                },
                children: [
                    props.subtitle && typeof props.title === 'string' ? /*#__PURE__*/ _jsx(Text, {
                        text: props.title,
                        role: "heading",
                        weight: "semibold",
                        color: isDark ? 'white' : theme.color.gray100,
                        size: 15,
                        style: {
                            lineHeight: 22,
                            textAlign: 'center',
                            marginTop: -10,
                            marginBottom: 10
                        }
                    }) : null,
                    typeof props.title === 'string' ? /*#__PURE__*/ _jsx(Text, {
                        text: props.subtitle || props.title,
                        role: "heading",
                        weight: "bold",
                        color: isDark ? 'white' : theme.color.gray100,
                        size: 28,
                        style: {
                            lineHeight: 35,
                            marginBottom: 30,
                            textAlign: 'center'
                        }
                    }) : props.title
                ]
            }),
            /*#__PURE__*/ _jsx(View, {
                style: {
                    padding: 20,
                    borderTopStartRadius: props.preview ? 0 : 30,
                    borderTopEndRadius: props.preview ? 0 : 30,
                    backgroundColor: props.secondaryColor || 'white',
                    marginTop: -30,
                    flexGrow: 1
                },
                children: props.children
            })
        ]
    });
    var _props_overflowColor, _props_overflowColor1, _ref;
    var content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            props.preview || !isFocused ? null : /*#__PURE__*/ _jsx(SystemBars, {
                style: isDark ? 'light' : 'dark'
            }),
            props.noScrollView ? inner : /*#__PURE__*/ _jsx(ScrollView, {
                testID: props.testID,
                topOverflowColor: (_props_overflowColor = props.overflowColor) !== null && _props_overflowColor !== void 0 ? _props_overflowColor : props.color,
                bottomOverflowColor: (_ref = (_props_overflowColor1 = props.overflowColor) !== null && _props_overflowColor1 !== void 0 ? _props_overflowColor1 : props.bottomSafeAreaColor) !== null && _ref !== void 0 ? _ref : props.secondaryColor,
                contentContainerStyle: {
                    flexGrow: 1
                },
                scrollEventThrottle: 8,
                onScroll: function(e) {
                    var options = navigationContainer === null || navigationContainer === void 0 ? void 0 : navigationContainer.getCurrentOptions();
                    var titleParam = options === null || options === void 0 ? void 0 : options.headerTitle;
                    if (e.nativeEvent.contentOffset.y > 62 && !titleParam) {
                        setOptions({
                            headerTitle: props.subtitle || props.title
                        });
                    } else if (e.nativeEvent.contentOffset.y < 62 && titleParam) {
                        setOptions({
                            headerTitle: ''
                        });
                    }
                },
                style: props.innerSafeArea ? {
                    paddingBottom: insets.bottom
                } : undefined,
                children: inner
            })
        ]
    });
    return props.innerSafeArea ? content : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(View, {
                style: {
                    flex: 1
                },
                testID: props.noScrollView ? props.testID : undefined,
                children: content
            }),
            /*#__PURE__*/ _jsx(View, {
                style: {
                    backgroundColor: props.bottomSafeAreaColor || props.secondaryColor,
                    paddingBottom: insets.bottom
                }
            })
        ]
    });
}
